/**
 * 主题样式注入器
 *
 * 用于强制覆盖 Element Plus 的样式，解决 CSS 变量优先级不够的问题。
 * 生成具体的 CSS 规则并注入到 document head 中。
 */

/**
 * 生成 Element Plus 覆盖样式
 * @param primaryColor 主色
 * @param successColor 成功色
 * @param warningColor 警告色
 * @param dangerColor 危险色
 */
export function generateElementPlusOverrideStyle(
  primaryColor: string,
  successColor: string = "#67C23A",
  warningColor: string = "#E6A23C",
  dangerColor: string = "#F56C6C",
): string {
  return `
    :root {
      --el-color-primary: ${primaryColor} !important;
      --el-color-success: ${successColor} !important;
      --el-color-warning: ${warningColor} !important;
      --el-color-danger: ${dangerColor} !important;
      
      /* 强制覆盖按钮样式 */
      --el-button-bg-color: #ffffff;
      --el-button-text-color: #606266;
      --el-button-border-color: #dcdfe6;
    }
    
    /* Primary Button 强制覆盖 */
    .el-button--primary {
      --el-button-bg-color: ${primaryColor} !important;
      --el-button-border-color: ${primaryColor} !important;
      --el-button-text-color: #ffffff !important;
      --el-button-hover-bg-color: var(--el-color-primary-light-3) !important;
      --el-button-hover-border-color: var(--el-color-primary-light-3) !important;
      --el-button-active-bg-color: var(--el-color-primary-dark-2) !important;
      --el-button-active-border-color: var(--el-color-primary-dark-2) !important;
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
    }
    
    /* Success Button */
    .el-button--success {
      --el-button-bg-color: ${successColor} !important;
      --el-button-border-color: ${successColor} !important;
      background-color: ${successColor} !important;
      border-color: ${successColor} !important;
    }
    
    /* Warning Button */
    .el-button--warning {
      --el-button-bg-color: ${warningColor} !important;
      --el-button-border-color: ${warningColor} !important;
      background-color: ${warningColor} !important;
      border-color: ${warningColor} !important;
    }
    
    /* Danger Button */
    .el-button--danger {
      --el-button-bg-color: ${dangerColor} !important;
      --el-button-border-color: ${dangerColor} !important;
      background-color: ${dangerColor} !important;
      border-color: ${dangerColor} !important;
    }

    /* Plain 按钮：全局实心背景会覆盖 Element Plus 的 plain 变体，导致文字与背景同色 */
    .el-button--primary.is-plain,
    .el-button--success.is-plain,
    .el-button--warning.is-plain,
    .el-button--danger.is-plain {
      background-color: transparent !important;
      --el-button-bg-color: transparent !important;
    }
    .el-button--primary.is-plain {
      color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
      --el-button-text-color: ${primaryColor} !important;
    }
    .el-button--success.is-plain {
      color: ${successColor} !important;
      border-color: ${successColor} !important;
      --el-button-text-color: ${successColor} !important;
    }
    .el-button--warning.is-plain {
      color: #8a5a00 !important;
      border-color: #d9a441 !important;
      --el-button-text-color: #8a5a00 !important;
      font-weight: 600;
    }
    .el-button--danger.is-plain {
      color: #b42318 !important;
      border-color: #e26d64 !important;
      --el-button-text-color: #b42318 !important;
      font-weight: 600;
    }
    .el-button--warning.is-plain:hover,
    .el-button--warning.is-plain:focus {
      color: #8a5a00 !important;
      border-color: #b7791f !important;
      background-color: #fdf6ec !important;
    }
    .el-button--danger.is-plain:hover,
    .el-button--danger.is-plain:focus {
      color: #b42318 !important;
      border-color: #d92d20 !important;
      background-color: #fef0f0 !important;
    }
    
    /* 链接文字颜色 */
    .el-link.el-link--primary {
      --el-link-text-color: ${primaryColor} !important;
      color: ${primaryColor} !important;
    }
    
    /* 标签页激活颜色 */
    .el-tabs__item.is-active {
      color: ${primaryColor} !important;
    }
    
    .el-tabs__active-bar {
      background-color: ${primaryColor} !important;
    }
    
    /* 单选框/多选框选中颜色 */
    .el-radio__input.is-checked .el-radio__inner,
    .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: ${primaryColor} !important;
      border-color: ${primaryColor} !important;
    }
    
    .el-radio__input.is-checked + .el-radio__label,
    .el-checkbox__input.is-checked + .el-checkbox__label {
      color: ${primaryColor} !important;
    }
    
    /* 菜单激活颜色 */
    .el-menu-item.is-active {
      color: ${primaryColor} !important;
    }
    
    /* 分页激活颜色 */
    .el-pagination.is-background .el-pager li.is-active {
      background-color: ${primaryColor} !important;
    }
  `;
}

/**
 * 注入样式到 head
 */
export function injectThemeStyle(
  styleContent: string,
  id: string = "pgy-theme-override",
) {
  // 先移除旧的样式标签
  const oldStyle = document.getElementById(id);
  if (oldStyle) {
    oldStyle.remove();
  }

  const styleEl = document.createElement("style");
  styleEl.id = id;
  // 添加时间戳属性，方便调试确认是否更新
  styleEl.setAttribute("data-timestamp", Date.now().toString());
  styleEl.textContent = styleContent;
  document.head.appendChild(styleEl);
}
